import { shallowEqual } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";

// Hooks
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Store
import { toggleLoader } from "store/slice/UI";
import { convertedOpenSearchData } from "store/slice/Team/team.functions";
import { DraxRole, getRoles, getRolesThunk } from "store/slice/Team/roles/rolesSlice";
import { fetchPersonSet } from "store/slice/Project/peopleSet/peopleSetSlice";
import { setShortList } from "store/slice/refinement/shortList/shortListSlice";
import { setLongListWithIn } from "store/slice/refinement/longList/longListSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { convertOpenSearchToMember } from "store/slice/Team/boardStructure/boardStructure.functions";
import { getProjectCompanyData, fetchProjectCompany } from "store/slice/Project/projectData/ProjectSlice";
import {
    fetchBoardStructure,
    getBoardStructureData,
    getTeamsCustomBoardStructure,
    setBoardStructure,
    setBoardStructureStatusDone,
    setCustomBoardStructure,
} from "store/slice/Team/boardStructure/boardStructureSlice";
import {
    fetchAllProjectSearchAis,
    fetchProjectSearchAi,
    getSearchAis,
} from "store/slice/Project/searchAis/searchAisSlice";

// Enums
import { urlPaths } from "enums/urlPaths";

// Utils
import { isEmpty } from "lodash";
import { requestPeopleData, talentPotentialMembersPayloadColumns } from "services/people/people.functions";
import {
    getPersonMetaUI,
    getInsightRelatedSltPath,
    getInitialFetchResponse,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
    getListsFetchResponse,
    TInitialFetch,
    TInitialFetchError,
    getInsightPeersPath,
} from "pages/ProjectDashboard/adapters/formattedData";
import { useRedirectOnProjectError } from "utils";

// Types
import {
    InsightData,
    ProjectDataPersonMeta,
    ProjectDataSimple,
    ProjectPersonSet,
    ProjectSearchAICreationResponse,
} from "services/projects/project.types";
import { TSearchAIs } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteSearchAIs";
import { fetchCompanySet } from "store/slice/Project/companiesSet/companiesSetSlice";
import { SelectChangeEvent } from "@mui/material";

// Abilities
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { flattenSectorProductsDomain } from "services";

const useSetupCompany = (): {
    shouldShow: boolean;
    shouldNotFetchBoard: boolean;
    hasRelatedLongList: boolean;
    currentSearchAI: number | undefined;
    searchAiOptions: TSearchAIs[];
    hasBoardMembers: boolean;
    companiesOptions: ProjectDataSimple[];
    selectedCompanyOption: number | undefined;
    handleSelectSearchAi: (searchAi: TSearchAIs | string | null) => void;
    handleChangeCompany: (event: SelectChangeEvent) => void;
} => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const ability = useAbility(AbilityContext);
    const isFreemium = !ability.can("see", "SAAS_LD_PROJECT_BRIEF_CREATE");

    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([companyId, projectId, insightId]);

    // Data from store
    const { data: searchAiList } = useAppSelector(getSearchAis);
    const boardStructure = useAppSelector(getBoardStructureData);
    const customBoard = useAppSelector(getTeamsCustomBoardStructure);
    const { data: roles } = useAppSelector(getRoles);
    const { data: companyData, status: companyStatus } = useAppSelector(getProjectCompanyData);
    const { data: insightData, status: insightStatus } = useAppSelector(
        getInsightById(Number(insightId)),
        shallowEqual
    );

    const [hasBoardMembers, setHasBoardMembers] = useState(true);
    const [shouldShow, setShouldShow] = useState(false);
    const [shouldNotFetchBoard, setShouldNotFetchBoard] = useState(false);
    const [currentSearchAI, setCurrentSearchAI] = useState<number | undefined>(
        searchAiId !== null ? Number(searchAiId) : undefined
    );

    const hasRelatedLongList = Boolean(searchAiId);

    // Companies options
    const [companiesOptions, setCompaniesOptions] = useState<ProjectDataSimple[]>([]);
    const [selectedCompanyOption, setSelectedCompanyOption] = useState<number>();

    // Search AI options
    const searchAiOptions = searchAiList
        ?.filter((searchAi) => {
            const searchAiData = getSearchAiLeadersLongList(searchAi);
            return searchAiData?.ref?.path;
        })
        ?.map((searchAi) => ({
            id: searchAi.data.id,
            name: searchAi.data.name,
        }));

    const handleSelectSearchAi = (searchAi: TSearchAIs | string | null) => {
        if (searchAi === null) {
            return;
        }
        const validSearchAi = typeof searchAi === "string" ? searchAi : searchAi.id;
        dispatch(setShortList([]));
        dispatch(setLongListWithIn([]));
        setCurrentSearchAI(Number(validSearchAi));
        updateLists(validSearchAi.toString(), insightData);
    };

    const getInsightBoard = async (insightData: InsightData | undefined) => {
        if (!insightData || projectId === null) return;

        const relatedPersonSetRef = getInsightRelatedSltPath(insightData)?.ref?.path;

        if (!relatedPersonSetRef) return;

        const boardSetList: ProjectPersonSet | undefined = unwrapResult(
            await dispatch(fetchPersonSet({ projectId, path: relatedPersonSetRef }))
        );

        return boardSetList;
    };

    const handleChangeCompany = async (event: SelectChangeEvent) => {
        if (!companyId || !projectId) {
            return;
        }

        const selectedId = event.target.value;
        setSelectedCompanyOption(parseInt(selectedId));

        dispatch(toggleLoader(true));

        await dispatch(fetchBoardStructure({ companyId: selectedId, projectId, isFreemium }));

        dispatch(toggleLoader(false));
    };

    // Fetch selected option data
    const updateLists = async (searchAiId: string | null, insight?: InsightData) => {
        if (!currentSearchAI || searchAiId === null || !projectId) {
            dispatch(setShortList([]));
            dispatch(setLongListWithIn([]));
            return;
        }

        dispatch(toggleLoader(true));

        const currentBoard = await getInsightBoard(insight);

        if (isEmpty(currentBoard)) {
            setHasBoardMembers(false);
            dispatch(toggleLoader(false));
            return;
        }

        const relatedSearchAi: ProjectSearchAICreationResponse = unwrapResult(
            await dispatch(
                fetchProjectSearchAi({
                    projectId: Number(projectId),
                    path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`,
                })
            )
        );

        const relatedSearchAiChildren = relatedSearchAi.children;
        if (!relatedSearchAiChildren?.length) {
            dispatch(toggleLoader(false));
            return;
        }

        const promises = [];
        const longlistPath = getSearchAiLeadersLongList(relatedSearchAi)?.ref?.path;
        const shortlistPath = getSearchAiLeadersShortList(relatedSearchAi)?.ref?.path;

        if (longlistPath) {
            promises.push(dispatch(fetchPersonSet({ projectId, path: longlistPath })));
        }

        if (shortlistPath) {
            promises.push(dispatch(fetchPersonSet({ projectId, path: shortlistPath })));
        }

        const response = await Promise.allSettled(promises);
        const resultListsData = getListsFetchResponse<ProjectPersonSet>(response);

        if ("error" in resultListsData) {
            dispatch(toggleLoader(false));
            console.error(resultListsData.reason);
            return;
        }

        const [longListSetList, shortListSetList] = resultListsData;

        const longList = longListSetList?.data?.person_ids?.map((id, i) => {
            const metaUI = getPersonMetaUI(longListSetList, id);
            return { ...metaUI, id };
        }) as ProjectDataPersonMeta[] | undefined;

        const shortList = shortListSetList?.data?.person_ids?.map((id, i) => {
            const metaUI = getPersonMetaUI(shortListSetList, id);
            return { ...metaUI, id };
        }) as ProjectDataPersonMeta[] | undefined;

        const ids = [...(shortList?.filter((s) => s.id) || []), ...(longList?.filter((s) => s.id) || [])].map(
            (e) => e.id
        );

        const resultPeopleData = await requestPeopleData(ids, talentPotentialMembersPayloadColumns);
        const peopleWithData = convertedOpenSearchData(resultPeopleData);

        if (shortList && currentBoard) {
            dispatch(
                setShortList(
                    peopleWithData
                        .filter((p: any) => shortList.map((e: any) => e.id).includes(Number(p.person_id)))
                        .map((member: any, i: number) => {
                            const memberMetaUI = shortList.find((e: any) => e.id === Number(member.person_id));
                            const boardToUse = (
                                customBoard.length
                                    ? customBoard
                                    : boardStructure.length
                                    ? boardStructure
                                    : currentBoard.data.person_ids
                            ) as any;
                            const isInBoard = boardToUse.find(
                                (board: any) =>
                                    (typeof board === "number" ? board : board.person_id) === Number(member.person_id)
                            );
                            return {
                                ...member,
                                person_id: Number(member.person_id),
                                in: "shortList",
                                statusUI: isInBoard ? ("disabled" as const) : ("enabled" as const),
                                ...(memberMetaUI?.role_id
                                    ? {
                                          roleKey: `${memberMetaUI.role_id}`,
                                          role: roles.find((r) => r.id === Number(memberMetaUI?.role_id))?.name,
                                      }
                                    : {}),
                            };
                        })
                )
            );
        }

        if (longList) {
            dispatch(
                setLongListWithIn(
                    peopleWithData
                        .filter((p: any) => longList.map((e: any) => e.id).includes(Number(p.person_id)))
                        .map((member: any, i: number) => {
                            const memberMetaUI = longList.find((e: any) => e.id === Number(member.person_id));
                            return {
                                ...member,
                                person_id: Number(member.person_id),
                                in: memberMetaUI?.in,
                                statusUI: "enabled" as const,
                                ...(memberMetaUI?.role_id
                                    ? {
                                          roleKey: `${memberMetaUI.role_id}`,
                                          role: roles.find((r) => r.id === Number(memberMetaUI?.role_id))?.name,
                                      }
                                    : {}),
                            };
                        })
                )
            );
        }

        dispatch(toggleLoader(false));
    };

    const getPeersList = async (
        insightData: InsightData | undefined,
        companyData: ReturnType<typeof flattenSectorProductsDomain> | undefined
    ) => {
        if (!companyId || !projectId || !insightData) return;

        const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;

        if (relatedPeersPath) {
            const currentCompany = { id: parseInt(companyId), name: companyData?.name ?? "" };
            const companies = unwrapResult(await dispatch(fetchCompanySet({ projectId, path: relatedPeersPath })));

            // Insight may not have Peers, companies.children might be undefined or null, therefore the ? and the []
            const peersData = companies.children?.map((c) => ({ id: c.data.id, name: c.data.name })) || [];

            setCompaniesOptions([currentCompany, ...peersData]);
            setSelectedCompanyOption(currentCompany.id);
        }
    };

    // Initial Fetch
    useEffect(() => {
        const hasCompanyData = !isEmpty(companyData);
        const hasInsightData = !isEmpty(insightData);

        const initialFetch = async () => {
            let result = {} as TInitialFetch | TInitialFetchError;
            const promises = [];

            if (!hasBoardMembers) {
                setHasBoardMembers(true);
            }

            if (!hasCompanyData && companyStatus !== "fetching" && companyId) {
                promises.push(dispatch(fetchProjectCompany(companyId)));
            }

            if (!hasInsightData && insightStatus !== "fetching" && projectId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (!roles.length) {
                promises.push(dispatch(getRolesThunk()));
            }

            if (promises.length && projectId) {
                dispatch(toggleLoader(true));
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const companyParam = "company" in result ? result.company : companyData;
            const insightParam = "insight" in result ? result.insight : insightData;
            const rolesParam = "roles" in result ? result.roles : roles;

            const wasDataset = await setData(insightParam, rolesParam);

            if (wasDataset) {
                await updateLists(searchAiId, insightParam);
                await getPeersList(insightParam, companyParam);
            }

            dispatch(toggleLoader(false));

            if (hasRelatedLongList) {
                await dispatch(
                    fetchAllProjectSearchAis({
                        projectId: Number(projectId),
                        path: `/project_${projectId}/search_ai_list`,
                    })
                );
            }
        };

        const setData = async (insightData: InsightData | undefined, roles: DraxRole[] | undefined) => {
            if (insightData === undefined || roles === undefined || projectId === null) return;

            dispatch(toggleLoader(true));

            // Board already loaded
            if (boardStructure.length) {
                dispatch(setBoardStructureStatusDone());
                setShouldShow(true);
                setShouldNotFetchBoard(true);
                return;
            }

            const insightBoard = await getInsightBoard(insightData);

            if (isEmpty(insightBoard)) {
                if (searchAiId === null) {
                    // It's a new SLT
                    setShouldShow(true);
                    setHasBoardMembers(true);
                } else {
                    // It's an existing SLT in Edit Team with empty board
                    setHasBoardMembers(false);
                }
                return;
            }

            // It's an existing SLT in Create Team with empty board
            if (!insightBoard?.data?.person_ids.length) {
                setShouldShow(true);
                return;
            }

            setShouldNotFetchBoard(true);
            setShouldShow(true);

            try {
                const boardStructureData = await requestPeopleData(
                    insightBoard?.data?.person_ids || [],
                    talentPotentialMembersPayloadColumns
                );

                const convertedBoardData = convertedOpenSearchData(boardStructureData);
                const members = convertOpenSearchToMember(
                    convertedBoardData,
                    insightBoard.meta_ui?.person_ids ?? [],
                    roles
                );

                if (searchAiId) {
                    const boardMembers = members.filter((member: any) => member?.meta?.movedFrom !== "shortList");
                    dispatch(setBoardStructure(boardMembers));
                    dispatch(setCustomBoardStructure(members));
                } else {
                    dispatch(setBoardStructure(members));
                }
            } catch (err) {
                console.error(err);
                return;
            }

            return true;
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        shouldShow,
        shouldNotFetchBoard,
        hasRelatedLongList,
        currentSearchAI,
        searchAiOptions,
        hasBoardMembers,
        companiesOptions,
        selectedCompanyOption,
        handleSelectSearchAi,
        handleChangeCompany,
    };
};

export default useSetupCompany;
